import { Box, Grid, Heading, HStack, Text, VStack } from "@chakra-ui/react";
import { motion, useReducedMotion, useScroll, useSpring } from "framer-motion";
import { useRef, type ReactNode } from "react";
import { portfolio, type ExperienceItem } from "../data/portfolio";
import { AppGrid } from "./AppsShowcase";
import { MotionFade } from "./MotionFade";
import { Accent, SectionHeading } from "./SectionHeading";

const MotionBox = motion(Box);

type CompanyGroup = {
  company: string;
  location?: string;
  roles: ExperienceItem[];
};

// Periods are stored as "início, fim" (most recent role first).
const splitPeriod = (period: string) => period.split(", ");

function groupByCompany(items: ExperienceItem[]): CompanyGroup[] {
  return items.reduce<CompanyGroup[]>((groups, item) => {
    const last = groups[groups.length - 1];
    if (last && last.company === item.company) {
      last.roles.push(item);
    } else {
      groups.push({ company: item.company, location: item.location, roles: [item] });
    }
    return groups;
  }, []);
}

function Period({ period }: { period: string }) {
  const [start, end] = splitPeriod(period);
  return (
    <Text fontFamily="mono" fontSize="xs" letterSpacing="0.1em" textTransform="uppercase" color="graphite.400">
      {end ? `${start} → ${end}` : start}
    </Text>
  );
}

function Role({ role, isCurrent, showPeriod }: { role: ExperienceItem; isCurrent: boolean; showPeriod: boolean }) {
  return (
    <MotionFade position="relative" pl={{ base: 7, md: 10 }}>
      <Box
        position="absolute"
        left="-5px"
        top="9px"
        w="11px"
        h="11px"
        borderRadius="full"
        bg={isCurrent ? "accent.400" : "graphite.950"}
        borderWidth="2px"
        borderColor="accent.400"
        boxShadow="0 0 0 5px #141419"
      />
      {showPeriod ? (
        <Box mb={2}>
          <Period period={role.period} />
        </Box>
      ) : null}
      <Heading as="h4" fontSize={{ base: "xl", md: "2xl" }} letterSpacing="-0.02em" color="white">
        {role.role}
      </Heading>
      <Text mt={3} lineHeight="tall" color="graphite.300" maxW="2xl">
        {role.summary}
      </Text>
      {role.highlights && role.highlights.length > 0 ? (
        <VStack as="ul" align="stretch" spacing={0} mt={5} listStyleType="none" maxW="2xl">
          {role.highlights.map((highlight, i) => (
            <HStack
              as="li"
              key={highlight}
              align="flex-start"
              spacing={4}
              py={3.5}
              borderTopWidth="1px"
              borderColor="whiteAlpha.100"
              transition="background-color 0.25s ease"
              _hover={{ bg: "whiteAlpha.50" }}
            >
              <Text fontFamily="mono" fontSize="xs" color="accent.300" mt="5px" flexShrink={0}>
                {String(i + 1).padStart(2, "0")}
              </Text>
              <Text color="graphite.100">{highlight}</Text>
            </HStack>
          ))}
        </VStack>
      ) : null}
      {role.appCount ? <AppGrid count={role.appCount} /> : null}
    </MotionFade>
  );
}

function Timeline({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.7", "end 0.6"] });
  const lineScale = useSpring(scrollYProgress, { stiffness: 90, damping: 24, mass: 0.4 });

  return (
    <Box ref={ref} position="relative">
      <Box position="absolute" left={0} top="12px" bottom={0} w="1px" bg="whiteAlpha.200">
        <MotionBox
          position="absolute"
          inset={0}
          bg="accent.400"
          style={prefersReducedMotion ? undefined : { scaleY: lineScale, transformOrigin: "center top" }}
        />
      </Box>
      <VStack align="stretch" spacing={{ base: 10, md: 12 }}>
        {children}
      </VStack>
    </Box>
  );
}

export function ExperienceSection() {
  const groups = groupByCompany(portfolio.experience);

  return (
    <Box id="experiencia" as="section" py={{ base: 20, md: 32 }} px={{ base: 5, md: 8 }} scrollMarginTop="72px">
      <Box maxW="container.xl" mx="auto">
        <SectionHeading
          section="experiencia"
          title={
            <>
              Do suporte de caixa ao <Accent>código de 60 apps</Accent>
            </>
          }
          lead="Comecei atendendo supermercados no frente de caixa, passei por infraestrutura e qualidade, e hoje respondo pelo desenvolvimento de um ecossistema mobile no setor financeiro."
        />

        <VStack align="stretch" spacing={{ base: 16, md: 24 }}>
          {groups.map((group, gi) => {
            const first = group.roles[group.roles.length - 1];
            const latest = group.roles[0];
            const [start] = splitPeriod(first.period);
            const [, end] = splitPeriod(latest.period);
            const isCurrentCompany = gi === 0 && end === "atual";
            const hasManyRoles = group.roles.length > 1;

            return (
              <Grid
                key={group.company}
                templateColumns={{ base: "1fr", lg: "300px minmax(0, 1fr)" }}
                gap={{ base: 8, lg: 12 }}
                alignItems="start"
              >
                <MotionFade position={{ lg: "sticky" }} top={{ lg: "120px" }}>
                  <Period period={`${start}, ${end}`} />
                  <Heading as="h3" fontSize={{ base: "3xl", md: "4xl" }} letterSpacing="-0.03em" color="white" mt={3}>
                    {group.company}
                  </Heading>
                  <HStack spacing={3} mt={3} flexWrap="wrap" color="graphite.400" fontSize="sm">
                    {group.location ? <Text as="span">{group.location}</Text> : null}
                    {hasManyRoles ? (
                      <Text as="span" color="accent.300">
                        {group.roles.length} funções na mesma empresa
                      </Text>
                    ) : null}
                    {isCurrentCompany ? (
                      <HStack spacing={2} color="green.300">
                        <Box w="7px" h="7px" borderRadius="full" bg="green.300" />
                        <Text as="span">Atual</Text>
                      </HStack>
                    ) : null}
                  </HStack>
                </MotionFade>

                <Timeline>
                  {group.roles.map((role, ri) => (
                    <Role
                      key={`${role.role}-${role.period}`}
                      role={role}
                      isCurrent={isCurrentCompany && ri === 0}
                      showPeriod={hasManyRoles}
                    />
                  ))}
                </Timeline>
              </Grid>
            );
          })}
        </VStack>
      </Box>
    </Box>
  );
}
